import React from "react"
import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from "remotion"
import type { Track, RenderPreferences } from "../../schemas"

type Crop = { timeMs: number; cropX: number; cropY: number; cropW: number; cropH: number }

/** Keyframes closer together than this are a cut in the crop path; it is eased over this long instead. */
export const REFRAME_EASE_MS = 300

/**
 * The crop at `timeMs` along keyframes sorted by time: linear between keyframes, except that a
 * change packed into less than REFRAME_EASE_MS (a speaker switch) eases over REFRAME_EASE_MS
 * centred on it, so the picture glides instead of jumping.
 * ponytail: eases that overlap a neighbouring keyframe are not blended with it; fine while switches are sparse.
 */
export function cropAt(sorted: Crop[], timeMs: number): Omit<Crop, "timeMs"> {
  let prevKf = sorted[0]
  let nextKf = sorted[sorted.length - 1]
  let progress = 0

  const abrupt = sorted.findIndex((kf, i) => {
    const next = sorted[i + 1]
    return next && next.timeMs - kf.timeMs < REFRAME_EASE_MS
      && Math.abs(timeMs - (kf.timeMs + next.timeMs) / 2) < REFRAME_EASE_MS / 2
  })
  if (abrupt >= 0) {
    prevKf = sorted[abrupt]
    nextKf = sorted[abrupt + 1]
    const mid = (prevKf.timeMs + nextKf.timeMs) / 2
    progress = interpolate(timeMs, [mid - REFRAME_EASE_MS / 2, mid + REFRAME_EASE_MS / 2], [0, 1], {
      easing: Easing.inOut(Easing.ease),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    })
  } else {
    for (let i = 0; i < sorted.length - 1; i++) {
      if (timeMs >= sorted[i].timeMs && timeMs <= sorted[i + 1].timeMs) {
        prevKf = sorted[i]
        nextKf = sorted[i + 1]
        break
      }
    }
    progress = prevKf.timeMs === nextKf.timeMs ? 0 : (timeMs - prevKf.timeMs) / (nextKf.timeMs - prevKf.timeMs)
  }

  return {
    cropX: interpolate(progress, [0, 1], [prevKf.cropX, nextKf.cropX]),
    cropY: interpolate(progress, [0, 1], [prevKf.cropY, nextKf.cropY]),
    cropW: interpolate(progress, [0, 1], [prevKf.cropW, nextKf.cropW]),
    cropH: interpolate(progress, [0, 1], [prevKf.cropH, nextKf.cropH]),
  }
}

interface ReframeLayerProps {
  reframeTrack: Track | undefined
  renderPref: RenderPreferences
  children: React.ReactNode
}

export const ReframeLayer: React.FC<ReframeLayerProps> = ({ reframeTrack, renderPref, children }) => {
  const frame = useCurrentFrame()
  const { fps, width, height } = useVideoConfig()

  // If no reframe track or not enabled, render children directly
  if (!reframeTrack || !renderPref.enableReframe) {
    return <AbsoluteFill>{children}</AbsoluteFill>
  }

  // Get reframe content from the first section/segment
  const segment = reframeTrack.sections[0]?.segments[0]
  const content = segment?.content
  if (!content) {
    return <AbsoluteFill>{children}</AbsoluteFill>
  }

  const keyframes = content.keyframes ?? []

  if (keyframes.length === 0) {
    // Default: center crop to target aspect ratio
    return <AbsoluteFill>{children}</AbsoluteFill>
  }

  // Sort keyframes by timeMs
  const sorted = [...keyframes].sort((a: any, b: any) => a.timeMs - b.timeMs)

  const { cropX, cropY, cropW, cropH } = cropAt(sorted, (frame / fps) * 1000)

  // Scale factor to fill the output dimensions from the crop region
  const scaleX = cropW > 0 ? width / (cropW * width) : 1
  const scaleY = cropH > 0 ? height / (cropH * height) : 1
  const scale = Math.max(scaleX, scaleY)

  // Translate to center the crop region
  const translateX = -(cropX * width * scale) + (width - width * scale) / 2
  const translateY = -(cropY * height * scale) + (height - height * scale) / 2

  return (
    <AbsoluteFill style={{ overflow: "hidden" }}>
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
          transformOrigin: "0 0",
        }}
      >
        {children}
      </div>
    </AbsoluteFill>
  )
}
